import { formatCpmCount, formatVolumeLabel } from '../../../constants/centralProductMaster.constants';
import type {
    CpmBarcodeThirdPartyClaim,
    CpmBarcodeThirdPartyClaimant,
    CpmMasterDetailsResponse,
} from '../../../types/centralProductMaster.types';

export function unionBarcodeThirdPartyClaims(
    sideA: CpmMasterDetailsResponse | null,
    sideB: CpmMasterDetailsResponse | null
): CpmBarcodeThirdPartyClaim[] {
    const groups = [
        ...(sideA?.barcodeThirdPartyClaims ?? []),
        ...(sideB?.barcodeThirdPartyClaims ?? []),
    ];
    const byBarcode = new Map<string, Map<string, CpmBarcodeThirdPartyClaimant>>();
    for (const group of groups) {
        const barcode = String(group.barcode ?? '').trim();
        if (barcode === '') continue;
        let claimants = byBarcode.get(barcode);
        if (claimants == null) {
            claimants = new Map();
            byBarcode.set(barcode, claimants);
        }
        for (const claimant of group.claimants ?? []) {
            const id = String(claimant.productVariantId ?? '').trim();
            if (id === '') continue;
            claimants.set(id, claimant);
        }
    }
    return [...byBarcode.entries()]
        .map(([barcode, claimants]) => ({
            barcode,
            claimants: [...claimants.values()],
        }))
        .filter((group) => group.claimants.length > 0);
}

function packName(claimant: CpmBarcodeThirdPartyClaimant): string {
    const variantName = String(claimant.variantName ?? '').trim();
    if (variantName !== '') return variantName;
    const productName = String(claimant.product?.name ?? '').trim();
    return productName !== '' ? productName : 'Unnamed pack';
}

function claimantMeta(claimant: CpmBarcodeThirdPartyClaimant): string {
    const brand = String(claimant.brand?.name ?? '').trim();
    const volume = formatVolumeLabel(claimant.volumeMl, claimant.volumeUnit);
    const hasVolume = volume !== '' && volume !== '—';
    if (brand && hasVolume) return `${brand}  ·  ${volume}`;
    if (hasVolume) return volume;
    return brand;
}

export interface BarcodeThirdPartyWarningProps {
    claims: CpmBarcodeThirdPartyClaim[];
}

export default function BarcodeThirdPartyWarning({ claims }: BarcodeThirdPartyWarningProps) {
    if (claims.length === 0) return null;

    const barcodeCount = claims.length;
    const subhead =
        barcodeCount === 1
            ? '1 barcode here is also on another live product.'
            : `${formatCpmCount(barcodeCount)} barcodes here are also on other live products.`;
    const intro =
        barcodeCount === 1
            ? 'Merging combines evidence from these two records onto the survivor. It does not assign this barcode to the survivor or take it off the other product. Until someone resolves the conflict on the Barcode Conflicts tab — by picking one owner or removing the barcode — this barcode stays in conflict, and both claims remain there after the merge.'
            : 'Merging combines evidence from these two records onto the survivor. It does not assign these barcodes to the survivor or take them off the other product. Until someone resolves the conflict on the Barcode Conflicts tab — by picking one owner or removing the barcode — these barcodes stay in conflict, and both claims remain there after the merge.';

    return (
        <div
            className="flex flex-col bg-[var(--cpm-warning-bg)] border border-[var(--cpm-warning-border)]"
            style={{
                borderRadius: 16,
                boxShadow: '0 1px 2px rgba(14,8,88,.04)',
                padding: '18px 20px',
                gap: 13,
            }}
        >
            <div className="flex items-start" style={{ gap: 11 }}>
                <span
                    className="inline-flex items-center justify-center shrink-0 bg-white border border-[var(--cpm-warning-border)] text-[var(--cpm-warning-text)]"
                    style={{ width: 30, height: 30, borderRadius: 8 }}
                >
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                        <path d="M12 9v4" />
                        <path d="M12 17h.01" />
                    </svg>
                </span>
                <div className="flex flex-col min-w-0" style={{ gap: 3 }}>
                    <div className="flex items-baseline flex-wrap" style={{ gap: 10 }}>
                        <span
                            className="uppercase text-[var(--cpm-warning-text)]"
                            style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em' }}
                        >
                            Also on another product
                        </span>
                        <span
                            className="text-[var(--cpm-warning-text)]"
                            style={{ fontSize: 12.5, fontWeight: 700 }}
                        >
                            {subhead}
                        </span>
                    </div>
                    <span
                        className="text-[var(--cpm-text-secondary)] text-pretty"
                        style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.5 }}
                    >
                        {intro}
                    </span>
                </div>
            </div>
            <div className="flex flex-col" style={{ gap: 9 }}>
                {claims.map((group) => (
                    <div
                        key={group.barcode}
                        className="flex flex-col bg-white border border-[var(--cpm-warning-border)]"
                        style={{ gap: 8, padding: '12px 14px', borderRadius: 12 }}
                    >
                        <div className="flex items-center" style={{ gap: 8 }}>
                            <span
                                className="inline-flex items-center justify-center shrink-0 bg-[var(--cpm-warning-bg)] text-[var(--cpm-warning-text)]"
                                style={{ width: 22, height: 22, borderRadius: 6 }}
                            >
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M3 5v14" />
                                    <path d="M8 5v14" />
                                    <path d="M12 5v14" />
                                    <path d="M17 5v14" />
                                    <path d="M21 5v14" />
                                </svg>
                            </span>
                            <span
                                className="inline-flex items-center tabular font-mono"
                                style={{
                                    padding: '4px 9px',
                                    borderRadius: 8,
                                    fontSize: 12.5,
                                    fontWeight: 800,
                                    border: '1px solid var(--cpm-border-default)',
                                    background: 'var(--cpm-surface-subtle)',
                                    color: 'var(--cpm-text-primary)',
                                }}
                            >
                                {group.barcode}
                            </span>
                        </div>
                        <div className="flex flex-col" style={{ gap: 6, paddingLeft: 30 }}>
                            {group.claimants.map((claimant) => {
                                const meta = claimantMeta(claimant);
                                return (
                                    <div
                                        key={claimant.productVariantId}
                                        className="flex items-baseline flex-wrap"
                                        style={{ gap: 9 }}
                                    >
                                        <span
                                            className="text-[var(--cpm-text-tertiary)] whitespace-nowrap"
                                            style={{ fontSize: 11, fontWeight: 700 }}
                                        >
                                            Also on
                                        </span>
                                        <span
                                            className="text-[var(--cpm-brand-navy)] text-pretty"
                                            style={{
                                                fontSize: 13,
                                                fontWeight: 800,
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            {packName(claimant)}
                                        </span>
                                        {meta !== '' ? (
                                            <span
                                                className="text-[var(--cpm-text-secondary)] tabular"
                                                style={{ fontSize: 11.5, fontWeight: 700 }}
                                            >
                                                {meta}
                                            </span>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
